import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';

/**
 * Revenue Allocation Store
 * Manages fixed fee revenue allocation data across percentage, worked hours, and worked amount modes
 */

export const useRevenueAllocationStore = defineStore('revenueAllocation', () => {
  // Constants for percentage validation
  const PERCENTAGE_TOTAL = 100;
  const PERCENTAGE_TOLERANCE = 0.1; // Allow 99.9% to 100.1% to account for rounding

  // State
  const chargeId = ref(null);
  const chargeAmount = ref(0);
  const allocationMode = ref(0); // 0: Percentage, 1: Worked Hours, 2: Worked Amount
  const allocationModeName = ref('');
  const allocations = ref([]);
  const percentageAllocations = ref([]); // Backup of allocations when in percentage mode
  const isBilled = ref(false);
  const hasTimeEntries = ref(false);
  const validationMessage = ref('');
  const loading = ref(false);
  const error = ref(null);

  // Constants for allocation modes
  const ALLOCATION_MODES = {
    PERCENTAGE: 0,
    WORKED_HOURS: 1,
    WORKED_AMOUNT: 2
  };
  const MODE_NAMES = {
    [ALLOCATION_MODES.PERCENTAGE]: 'Percentage',
    [ALLOCATION_MODES.WORKED_HOURS]: 'Worked Hours',
    [ALLOCATION_MODES.WORKED_AMOUNT]: 'Worked Amount'
  };
  const ALLOCATION_MODE_VALUES = {
    [ALLOCATION_MODES.PERCENTAGE]: 'Percentage',
    [ALLOCATION_MODES.WORKED_HOURS]: 'WorkedHour',
    [ALLOCATION_MODES.WORKED_AMOUNT]: 'WorkedAmount'
  };

  // Reverse mapping for API string values to numeric mode
  const ALLOCATION_MODE_FROM_API = {
    percentage: ALLOCATION_MODES.PERCENTAGE,
    Percentage: ALLOCATION_MODES.PERCENTAGE,
    workedhour: ALLOCATION_MODES.WORKED_HOURS,
    WorkedHour: ALLOCATION_MODES.WORKED_HOURS,
    workedHour: ALLOCATION_MODES.WORKED_HOURS,
    workedHours: ALLOCATION_MODES.WORKED_HOURS,
    workedamount: ALLOCATION_MODES.WORKED_AMOUNT,
    WorkedAmount: ALLOCATION_MODES.WORKED_AMOUNT,
    workedAmount: ALLOCATION_MODES.WORKED_AMOUNT
  };

  // Computed properties
  const totalAmount = computed(() => {
    return allocations.value.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
  });
  const totalPercentage = computed(() => {
    return allocations.value.reduce((sum, a) => sum + (Number(a.percentage) || 0), 0);
  });
  const totalWorkedHours = computed(() => {
    return allocations.value.reduce((sum, a) => sum + (Number(a.workedHours) || 0), 0);
  });
  const totalWorkedAmount = computed(() => {
    return allocations.value.reduce((sum, a) => sum + (Number(a.workedAmount) || 0), 0);
  });
  const isAllocationValid = computed(() => {
    // If no allocations, always valid (can navigate and edit)
    if (allocations.value.length === 0) return true;

    // In percentage mode, validate that percentages are within tolerance range (99.9% to 100.1%)
    // This allows for rounding errors when splitting amounts
    if (allocationMode.value === ALLOCATION_MODES.PERCENTAGE) {
      // Round total percentage to 2 decimals for comparison
      const roundedTotal = Math.round(totalPercentage.value * 100) / 100;
      const minValid = PERCENTAGE_TOTAL - PERCENTAGE_TOLERANCE;
      const maxValid = PERCENTAGE_TOTAL + PERCENTAGE_TOLERANCE;
      return roundedTotal >= minValid && roundedTotal <= maxValid;
    }

    // For other modes, check if amounts total to the charge amount
    // Use a higher tolerance (0.02) to account for rounding and floating point precision
    const AMOUNT_TOLERANCE = 0.02;
    return Math.abs(totalAmount.value - chargeAmount.value) <= AMOUNT_TOLERANCE;
  });
  const isReadOnly = computed(() => allocationMode.value !== ALLOCATION_MODES.PERCENTAGE);
  const displayMode = computed(() => MODE_NAMES[allocationMode.value] || 'Unknown');

  // Actions
  const initializeStore = () => {
    // Initialize with empty state
    chargeId.value = null;
    chargeAmount.value = 0;
    allocationMode.value = ALLOCATION_MODES.PERCENTAGE;
    allocationModeName.value = MODE_NAMES[ALLOCATION_MODES.PERCENTAGE];
    allocations.value = [];
    percentageAllocations.value = [];
    isBilled.value = false;
    hasTimeEntries.value = true;
    validationMessage.value = '';
  };
  const loadAllocationData = async (chargeIdParam, users = []) => {
    loading.value = true;
    error.value = null;
    try {
      // On initial load, fetch with the default Percentage mode first
      const response = await axios.get(`/charges/${chargeIdParam}/allocation`, {
        params: {
          fixedFeeAllocationMode: 'Percentage'
        }
      });
      const data = response.data;

      // Update store with API response data
      chargeId.value = data.chargeId || chargeIdParam;
      chargeAmount.value = data.chargeAmount || data.amount || 0;

      // Convert allocation mode from API string format to numeric value (only on initial load)
      let numericMode = ALLOCATION_MODES.PERCENTAGE;
      if (data.allocationMode) {
        numericMode = ALLOCATION_MODE_FROM_API[data.allocationMode] ?? ALLOCATION_MODES.PERCENTAGE;
      }
      allocationMode.value = numericMode;
      allocationModeName.value = MODE_NAMES[numericMode] || 'Percentage';
      const mappedAllocations = Array.isArray(data.allocations) ? data.allocations.map(allocation => {
        const user = users.find(u => u.id === allocation.accountId);
        return {
          ...allocation,
          accountName: user ? user.name : allocation.accountName || 'Unknown User'
        };
      }) : [];
      allocations.value = mappedAllocations;
      percentageAllocations.value = [...mappedAllocations];
      isBilled.value = data.isBilled || false;
      hasTimeEntries.value = data.hasTimeEntries !== undefined ? data.hasTimeEntries : true;
      validationMessage.value = data.validationMessage || '';
    } catch (err) {
      error.value = err.message || 'Failed to load allocation data';
      console.error('Error loading allocation data:', err);
    } finally {
      loading.value = false;
    }
  };

  // Load allocations for a specific mode (without changing allocationMode itself)
  const loadAllocationsForMode = async (chargeIdParam, mode, users = [], newFixedFeeAmount = null) => {
    loading.value = true;
    error.value = null;
    try {
      // Determine the allocation mode value for the API query parameter
      const modeValue = ALLOCATION_MODE_VALUES[mode] || 'Percentage';

      // Build query parameters
      const params = {
        fixedFeeAllocationMode: modeValue
      };

      // Include newFixedFeeAmount as query parameter if provided to get preview of allocations with new amount
      if (newFixedFeeAmount !== null && newFixedFeeAmount !== undefined) {
        params.newFixedFeeAmount = newFixedFeeAmount;
      }

      // Call the API endpoint with the specified mode
      const response = await axios.get(`/charges/${chargeIdParam}/allocation`, {
        params
      });
      const data = response.data;

      // For non-Percentage modes: clear allocations completely before loading API data
      // This ensures we show ONLY the API-loaded data, not any percentage allocations
      if (mode !== ALLOCATION_MODES.PERCENTAGE) {
        allocations.value = [];
      }

      // Update only the allocations and related data, NOT the mode itself
      // The mode was already set by setAllocationMode before this was called

      // For percentage mode, always update from API response
      // If API returns no allocations, clear them (don't restore from backup)
      if (mode === ALLOCATION_MODES.PERCENTAGE) {
        const mappedAllocations = Array.isArray(data.allocations) ? data.allocations.map(allocation => {
          const user = users.find(u => u.id === allocation.accountId);
          return {
            ...allocation,
            accountName: user ? user.name : allocation.accountName || 'Unknown User'
          };
        }) : [];
        allocations.value = mappedAllocations;
        percentageAllocations.value = [...mappedAllocations];
      } else {
        // For non-percentage modes, always update allocations from API
        const mappedAllocations = Array.isArray(data.allocations) ? data.allocations.map(allocation => {
          const user = users.find(u => u.id === allocation.accountId);
          return {
            ...allocation,
            accountName: user ? user.name : allocation.accountName || 'Unknown User'
          };
        }) : [];
        allocations.value = mappedAllocations;
      }
      isBilled.value = data.isBilled || false;
      hasTimeEntries.value = data.hasTimeEntries !== undefined ? data.hasTimeEntries : true;
      validationMessage.value = data.validationMessage || '';
    } catch (err) {
      error.value = err.message || 'Failed to load allocation data';
      console.error('Error loading allocation data:', err);
      // For non-Percentage modes with API errors, ensure allocations is empty
      if (mode !== ALLOCATION_MODES.PERCENTAGE) {
        allocations.value = [];
      }
    } finally {
      loading.value = false;
    }
  };
  const setAllocationMode = mode => {
    // Always sync the backup before leaving Percentage mode
    if (allocationMode.value === ALLOCATION_MODES.PERCENTAGE && mode !== ALLOCATION_MODES.PERCENTAGE) {
      // Save percentage allocations before switching away
      percentageAllocations.value = [...allocations.value];
      // Clear allocations for non-Percentage modes - they will be loaded from API
      allocations.value = [];
    }
    // If switching back to Percentage, don't restore from backup - load fresh from API
    else if (allocationMode.value !== ALLOCATION_MODES.PERCENTAGE && mode === ALLOCATION_MODES.PERCENTAGE) {
      // Don't restore from backup - let the API call determine what to show
      allocations.value = [];
    }
    allocationMode.value = mode;
    allocationModeName.value = MODE_NAMES[mode];
  };
  const updateAllocations = newAllocations => {
    allocations.value = newAllocations;
    // If we're in percentage mode, also update the backup
    if (allocationMode.value === ALLOCATION_MODES.PERCENTAGE) {
      percentageAllocations.value = [...newAllocations];
    }
    // Clear any validation message from API when user manually updates allocations
    validationMessage.value = '';
  };
  const addAllocation = account => {
    const exists = allocations.value.some(a => a.accountId === account.id);
    if (!exists) {
      const newAllocation = {
        accountId: account.id,
        accountName: account.name,
        amount: 0,
        percentage: 0,
        workedHours: 0,
        workedAmount: 0,
        main: false
      };
      allocations.value.push(newAllocation);
      // If in percentage mode, also add to backup
      if (allocationMode.value === ALLOCATION_MODES.PERCENTAGE) {
        percentageAllocations.value.push({
          ...newAllocation
        });
      }
      // Clear validation message when user adds allocations
      validationMessage.value = '';
    }
  };
  const removeAllocation = accountId => {
    const index = allocations.value.findIndex(a => a.accountId === accountId);
    if (index > -1) {
      allocations.value.splice(index, 1);
      // If in percentage mode, also remove from backup
      if (allocationMode.value === ALLOCATION_MODES.PERCENTAGE) {
        const backupIndex = percentageAllocations.value.findIndex(a => a.accountId === accountId);
        if (backupIndex > -1) {
          percentageAllocations.value.splice(backupIndex, 1);
        }
      }
      // Clear validation message when user removes allocations
      validationMessage.value = '';
    }
  };
  const updateAllocationField = (accountId, field, value) => {
    // Special handling for the 'main' field - ensure only one allocation can be main
    if (field === 'main' && value === true) {
      // Set all allocations to main: false first
      allocations.value.forEach(a => {
        a.main = false;
      });
      // Also update backup if in percentage mode
      if (allocationMode.value === ALLOCATION_MODES.PERCENTAGE) {
        percentageAllocations.value.forEach(a => {
          a.main = false;
        });
      }
    }
    const allocation = allocations.value.find(a => a.accountId === accountId);
    if (allocation) {
      allocation[field] = value;
      // If in percentage mode, also update the backup
      if (allocationMode.value === ALLOCATION_MODES.PERCENTAGE) {
        const backupAllocation = percentageAllocations.value.find(a => a.accountId === accountId);
        if (backupAllocation) {
          backupAllocation[field] = value;
        }
      }
      // Clear validation message when user updates fields
      validationMessage.value = '';
    }
  };
  const resetToDefaults = async (chargeIdParam, users = [], companyDefaultMode = null, newFixedFeeAmount = null) => {
    loading.value = true;
    error.value = null;
    try {
      // If this is a new (unsaved) charge, do NOT call the API with "/new".
      // Instead, reset locally to company default mode.
      const effectiveId = chargeIdParam ?? chargeId.value;
      if (effectiveId === 'new' || !effectiveId) {
        // Local reset behavior for new charge - use company default mode
        const resetMode = companyDefaultMode ?? ALLOCATION_MODES.PERCENTAGE;
        allocationMode.value = resetMode;
        allocationModeName.value = MODE_NAMES[resetMode];
        // Clear current allocations; parent/component will seed invoice user if needed
        allocations.value = [];
        percentageAllocations.value = [];
        validationMessage.value = '';
        isBilled.value = false;
        // We don't change chargeAmount here; component may provide fixedFeeAmount
        loading.value = false;
        return;
      }

      // Build query parameters
      const params = {};

      // Include newFixedFeeAmount as query parameter if provided
      if (newFixedFeeAmount !== null && newFixedFeeAmount !== undefined) {
        params.newFixedFeeAmount = newFixedFeeAmount;
      }
      const response = await axios.post(`/charges/${effectiveId}/allocation/restore-defaults`, params, {});
      const data = response.data;

      // Update store with API response data
      chargeId.value = data.chargeId || chargeIdParam || chargeId.value;
      chargeAmount.value = data.chargeAmount || data.amount || 0;

      // Convert allocation mode from API string format to numeric value
      let numericMode = ALLOCATION_MODES.PERCENTAGE;
      if (data.allocationMode) {
        numericMode = ALLOCATION_MODE_FROM_API[data.allocationMode] ?? ALLOCATION_MODES.PERCENTAGE;
      }
      allocationMode.value = numericMode;
      allocationModeName.value = MODE_NAMES[numericMode] || 'Percentage';

      // Update allocations with the response data
      const mappedAllocations = Array.isArray(data.allocations) ? data.allocations.map(allocation => {
        const user = users.find(u => u.id === allocation.accountId);
        return {
          ...allocation,
          accountName: user ? user.name : allocation.accountName || 'Unknown User'
        };
      }) : [];
      allocations.value = mappedAllocations;

      // If in percentage mode, also update the backup
      if (allocationMode.value === ALLOCATION_MODES.PERCENTAGE) {
        percentageAllocations.value = [...mappedAllocations];
      }
      isBilled.value = data.isBilled || false;
      hasTimeEntries.value = data.hasTimeEntries !== undefined ? data.hasTimeEntries : true;
      validationMessage.value = data.validationMessage || '';
    } catch (err) {
      error.value = err.message || 'Failed to restore default allocations';
      console.error('Error restoring default allocations:', err);
    } finally {
      loading.value = false;
    }
  };
  const validateAndSave = async () => {
    if (!isAllocationValid.value) {
      error.value = `Invalid allocation. Amounts must total the fixed fee amount of ${chargeAmount.value.toFixed(2)}.`;
      return false;
    }
    try {
      // TODO: Replace with actual API call
      // await api.saveRevenueAllocation(chargeId.value, { allocations: allocations.value });
      return true;
    } catch (err) {
      error.value = err.message;
      return false;
    }
  };
  return {
    // State
    chargeId,
    chargeAmount,
    allocationMode,
    allocationModeName,
    allocations,
    percentageAllocations,
    isBilled,
    hasTimeEntries,
    validationMessage,
    loading,
    error,
    // Constants
    ALLOCATION_MODES,
    MODE_NAMES,
    ALLOCATION_MODE_VALUES,
    ALLOCATION_MODE_FROM_API,
    // Computed
    totalAmount,
    totalPercentage,
    totalWorkedHours,
    totalWorkedAmount,
    isAllocationValid,
    isReadOnly,
    displayMode,
    // Actions
    initializeStore,
    loadAllocationData,
    loadAllocationsForMode,
    setAllocationMode,
    updateAllocations,
    addAllocation,
    removeAllocation,
    updateAllocationField,
    resetToDefaults,
    validateAndSave
  };
});